// Collection page (sections/collection.liquid): filters, sorting, active-filter chips and pagination reload only this
// section (Section Rendering API) and keep the URL in sync; the filter drawer on mobile; one open dropdown at a time.
(() => {
  if (customElements.get('collection-page')) return;

  class CollectionPage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-cp-form]') || event.target.matches('[data-cp-sort]')) this.schedule(event.target.type === 'number' ? 600 : 0);
      });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-cp-link]');
        if (link) { event.preventDefault(); this.load(link.href); return; }
        if (event.target.closest('[data-cp-open]')) { this.toggle(true, true); return; }
        if (event.target.closest('[data-cp-close]')) this.toggle(false, true);
      });
      // Dropdown layout: opening one filter closes the others; a click outside closes them.
      this.addEventListener('toggle', (event) => {
        if (this.dataset.layout !== 'dropdown' || !event.target.open || window.matchMedia('(max-width: 749px)').matches) return;
        this.querySelectorAll('[data-cp-filter][open]').forEach((filter) => { if (filter !== event.target) filter.open = false; });
      }, true);
      this.outside = (event) => {
        if (this.dataset.layout !== 'dropdown' || event.target.closest('[data-cp-filter]')) return;
        this.querySelectorAll('[data-cp-filter][open]').forEach((filter) => { filter.open = false; });
      };
      document.addEventListener('click', this.outside);
      this.keys = (event) => { if (event.key === 'Escape' && this.classList.contains('is-open')) this.toggle(false, true); };
      document.addEventListener('keydown', this.keys);
      this.onPop = () => this.load(window.location.href, false);
      window.addEventListener('popstate', this.onPop);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.outside);
      document.removeEventListener('keydown', this.keys);
      window.removeEventListener('popstate', this.onPop);
    }

    // moveFocus (the shopper's own open / close, not the re-render after filtering): focus goes into the mobile drawer
    // when it opens and back to the Filter button when it closes, so keyboard users stay in the right place
    toggle(open, moveFocus = false) {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const wasInside = this.querySelector('aside')?.contains(document.activeElement);
      this.classList.toggle('is-open', open);
      this.querySelector('[data-cp-open]')?.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('modal-lock', open && mobile);
      if (!moveFocus || !mobile) return;
      if (open) this.querySelector('[data-cp-close]')?.focus({ preventScroll: true });
      else if (wasInside) this.querySelector('[data-cp-open]')?.focus({ preventScroll: true });
    }

    schedule(delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        const form = this.querySelector('[data-cp-form]');
        const params = new URLSearchParams();
        if (form) new FormData(form).forEach((value, key) => { if (String(value).trim() !== '') params.append(key, value); });
        const sort = this.querySelector('[data-cp-sort]');
        if (sort) params.set('sort_by', sort.value);
        this.load(`${this.dataset.url}?${params.toString()}`);
      }, delay);
    }

    async load(href, push = true) {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('section_id');
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
      this.classList.add('is-loading');
      try {
        const html = await fetch(fetchUrl, { headers: { Accept: 'text/html' } }).then((response) => response.text());
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('collection-page');
        if (!next) throw new Error('No section');
        // Keep which filter groups are open and the drawer state.
        const open = [...this.querySelectorAll('[data-cp-filter]')].map((filter) => filter.open);
        const drawerOpen = this.classList.contains('is-open');
        const scroll = this.querySelector('.cp__form')?.scrollTop || 0;
        ['.cp__toolbar', '[data-cp-results]', '[data-cp-filters]'].forEach((selector) => {
          const current = this.querySelector(selector);
          const replacement = next.querySelector(selector);
          if (current && replacement) current.replaceWith(replacement);
        });
        this.querySelectorAll('[data-cp-filter]').forEach((filter, i) => { if (open[i] !== undefined && window.matchMedia('(max-width: 749px)').matches) filter.open = open[i]; });
        if (this.dataset.layout === 'dropdown' && !window.matchMedia('(max-width: 749px)').matches) this.querySelectorAll('[data-cp-filter]').forEach((filter, i) => { filter.open = Boolean(open[i]); });
        this.toggle(drawerOpen);
        const form = this.querySelector('.cp__form');
        if (form) form.scrollTop = scroll;
        if (push) window.history.pushState({}, '', url);
        if (!drawerOpen && url.searchParams.has('page')) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        window.location.assign(url);
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  customElements.define('collection-page', CollectionPage);
})();
